/**
 * Act IV — Land and Kings (Joshua – 1 Kings).
 *
 * The promise finally lands somewhere. Four chapters: the crossing and the
 * conquest, the long anarchy of the judges, the making of David, and the one
 * golden generation that ends in a split.
 *
 * Chapter ids and verseIds are stable — progress is persisted against them, so
 * they must not be renamed.
 *
 * Scripture quoted inside scenes is the World English Bible (British Edition),
 * public domain, wrapped in *asterisks* so the renderer can set it in serif.
 */

export const ACT_KINGDOM = {
  id: 'kingdom',
  title: 'Land and Kings',
  summary: 'A home at last, a demand for a king, and a throne that never quite holds.',
  span: 'Joshua – 1 Kings',
  chapters: [
    // ── 1 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-joshua',
      title: 'Across the River',
      where: 'Joshua 1–6, 11, 24',
      era: 'c. 1400 BC',
      artCaption: 'A river stopped at flood stage, and a scarlet cord in a window',
      scenes: [
        "Moses is dead. Joshua takes over a nation standing at a border. One phrase keeps landing on him: be strong and courageous. It comes four times. The reason given is never his own ability. *Don’t be afraid. Don’t be dismayed, for the LORD your God is with you wherever you go.* His name is worth noticing. In Hebrew it is Yehoshua, which means the LORD saves. Carried across into Greek, the same name comes out as Jesus. A carpenter’s son in Nazareth will be given it more than a thousand years later.",

        "The first obstacle is water again. Israel reaches the Jordan in spring, at harvest, when the river runs high and spills over its banks. The priests carry the ark in first, and the flow stops. It piles up, the text says, some twenty miles upstream at a town called Adam. That stretch of the valley has soft crumbling cliffs, and landslides have blocked the river there before. An earthquake shut it off for twenty-one hours in 1927. Some readers take that as the mechanism. Joshua is interested in the timing.",

        "On the far bank, twelve men each carry a stone out of the dry riverbed and stack them at Gilgal. When children ask what the pile is for, somebody has to tell the story again. Then the manna stops. Forty years of food arriving from the sky simply ends, the day after they eat food grown in the land. And Joshua meets a man holding a drawn sword. Are you for us, or for our enemies? *No,* comes the reply. Neither. He has come as commander of the LORD’s army. Joshua takes off his sandals.",

        "Jericho falls without a battering ram. Seven days of walking, and a shout. The instruments are rams’ horns, the kind blown at festivals, not war trumpets. The army is keeping a religious calendar rather than a military one. The town is small and very old. It sits about 250 metres below sea level, the lowest inhabited place on earth. One household is spared: Rahab, a foreign prostitute who hid the spies and hung a scarlet cord in her window. Matthew later lists her in the family tree of Jesus, and makes no comment about it.",

        "Then the hardest material in the Old Testament. Whole towns are put under a ban — everything in them destroyed, nothing kept back — and the book reports it without flinching. Readers have never agreed how to take it. Some hear the sweeping totals as ordinary ancient war talk, which routinely claimed enemies were wiped out when they plainly survived. Others read it straight, as judgement on one particular society. Joshua 11 says he took the whole land. Judges 1 opens with a list of what was not taken. Both sit a few pages apart.",

        "At the end, old and near death, Joshua gathers the tribes at Shechem. Put away the gods your fathers served across the river, he tells them. Which means they still had them, packed in the luggage, after everything. Choose today whom you will serve. Then, before anyone can answer, he goes first: *but as for me and my house, we will serve the LORD.* The chapter closes with a burial. Joseph’s bones, carried out of Egypt and kept the whole way, go into the ground. The loyalty will not outlast the people who swore it.",
      ],
      verseId: 'jos-1-9',
      takeaway: 'Courage in the Bible is not self-belief; it is belief that God is present.',
      questions: [
        {
          id: 'sq-joshua-1',
          question: 'What reason does God give Joshua for being strong and courageous?',
          options: [
            'That his army outnumbered the Canaanites',
            'That Moses had trained him personally',
            'That the LORD his God would be with him wherever he went',
            'That the walls of Jericho were already crumbling',
          ],
          answer: 2,
          explanation:
            'Joshua 1:9. The command is never “be brave because you are capable” — it is “be brave because I am with you”, which is a different kind of confidence and rests on somebody else.',
        },
        {
          id: 'sq-joshua-2',
          question: 'What was the state of the Jordan when Israel crossed it?',
          options: [
            'In flood, swollen and over its banks at harvest time',
            'Dried out after years of drought',
            'Frozen hard enough to walk on',
            'Dammed upstream by the Canaanites to stop them',
          ],
          answer: 0,
          explanation:
            'Joshua 3 notes that the river overflows all through the harvest season, so the crossing is set at the worst month of the year. The water is said to have piled up some twenty miles upstream, near a town called Adam.',
        },
        {
          id: 'sq-joshua-3',
          question: 'Joshua meets a man with a drawn sword and asks whose side he is on. What answer does he get?',
          options: [
            'That he has come to fight for Israel',
            'That he has come to fight for Jericho',
            'That he will decide once the battle starts',
            'Neither one — he has come as commander of the LORD’s army',
          ],
          answer: 3,
          explanation:
            'The question assumes God takes sides in a human contest; the reply refuses the frame and reverses it, asking instead who is under whose command. Joshua’s response is to take off his shoes, exactly as Moses did at the burning bush.',
        },
        {
          id: 'sq-joshua-4',
          question: 'Joshua’s name in Hebrew is Yehoshua. What does it become in Greek?',
          options: [
            'Joseph',
            'Jesus',
            'Hosea',
            'Elisha',
          ],
          answer: 1,
          explanation:
            'It means “the LORD saves”, and Greek has only one spelling for it — which is why older English Bibles occasionally print “Jesus” in a passage that is plainly about Joshua. The man who leads Israel into the land shares a name with the man Christians say leads them home.',
        },
      ],
    },

    // ── 2 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-judges',
      title: 'Everyone Did What Was Right',
      where: 'Judges, Ruth',
      era: 'c. 1375–1050 BC',
      artCaption: "A foreign widow gleaning barley at the edge of somebody else's field",
      scenes: [
        "Judges runs a loop, and each turn of it is worse. The people abandon God. A neighbouring power moves in on them. They cry out. God raises up a rescuer. There is peace. Then the rescuer dies and it starts again. The English word judge misleads. The word behind it means something nearer to a chieftain who puts things right — a local strongman with a mandate, not a magistrate in a courtroom. Twelve are named across the book. Gideon, Deborah, Samson. Some are heroic. Several are appalling.",

        "Deborah is the exception in every direction. She is a prophetess, and the only one in the book actually shown hearing cases — sitting under a palm tree while people bring her their disputes. She sends Barak against Sisera and his nine hundred iron chariots. Barak refuses to go without her, so she tells him the honour will go to a woman. It does, though not to her. Sisera runs from the battle into the tent of a woman named Jael, who gives him milk, and a blanket, and then a tent peg.",

        "Gideon first appears threshing wheat inside a winepress, hiding his own harvest from raiders. He is greeted as a mighty man of valour — encouragement, or sarcasm. God then cuts his army from thirty-two thousand down to three hundred, so nobody can claim the win. He wins. Offered the crown, he turns it down in the right words: the LORD will rule over you. Then he melts the plundered gold into a priest’s vest, and the country worships it. He has seventy sons; one more, Abimelech, later kills them on one stone. The youngest escapes.",

        "It gets darker. Jephthah promises God he will sacrifice whatever comes out of his door to meet him. His daughter comes out. The narrator reports it and offers no comment. Samson gets four chapters, more than any other judge, and never leads an army. Huge strength, no self-control, a run of private grudges, and more killed in his death than in his life. The book’s verdict on its own age is one repeated line: *In those days there was no king in Israel. Everyone did that which was right in his own eyes.*",

        "Set in the middle of that chaos is Ruth. Four quiet chapters, the same period. It opens with a famine in Bethlehem, a town whose name means house of bread. A family leaves for Moab. The men all die. A foreign widow refuses to leave her mother-in-law and walks back to a village with no reason to want her. She gleans in a field. That is law, not charity: Israel left the corners of the field and the dropped grain for widows, the poor and foreigners. A man named Boaz keeps that law generously.",

        "Ruth’s nationality is the sharp edge of the story. Deuteronomy barred Moabites from the assembly of the LORD for ten generations. Ruth is a Moabite. The book closes by tracing her line four generations down to David. Matthew names her in the family tree of Jesus, in the same verse as Rahab of Jericho. Judges leaves its own diagnosis hanging. There was no king in Israel. The next book has the people demanding one — and it is not obvious that either book thinks a king is the cure.",
      ],
      verseId: 'rut-1-16',
      takeaway: 'When everyone does what seems right to them, ordinary loyalty becomes extraordinary.',
      questions: [
        {
          id: 'sq-judges-1',
          question: 'How does the book of Judges summarise its own era?',
          options: [
            'The land had rest for forty years',
            'Everyone did that which was right in his own eyes',
            'They served the LORD all their days',
            'There was no prophet left in the land',
          ],
          answer: 1,
          explanation:
            'It appears at Judges 17:6 and again as the closing line of the book, both times paired with the observation that there was no king in Israel. It is a diagnosis, not a compliment.',
        },
        {
          id: 'sq-judges-2',
          question: 'What did the Hebrew title translated “judge” actually describe?',
          options: [
            'A priest who settled disputes at the tabernacle',
            'A scribe who copied and interpreted the law',
            'A local leader and deliverer who set things right, only rarely in a courtroom',
            'A judge appointed by the high priest to serve for life',
          ],
          answer: 2,
          explanation:
            'The word is *shophet*, from a root that resurfaces centuries later in Carthage as the title of its chief officials. Deborah is the only one in the book shown hearing cases at all; the rest lead and fight.',
        },
        {
          id: 'sq-judges-3',
          question: 'Why is Ruth’s nationality significant?',
          options: [
            'She is a Moabite, and Moabites were barred from the assembly — yet she becomes David’s great-grandmother',
            'She is an Egyptian, and Israel had been enslaved in Egypt',
            'She is a Canaanite from Jericho, like Rahab',
            'She is a Philistine from Gath, the town Goliath came from',
          ],
          answer: 0,
          explanation:
            'Deuteronomy 23:3 excludes Moabites to the tenth generation, and Ruth 4 traces her line to David anyway in four. The Bible sets the rule and the exception side by side without smoothing either of them out.',
        },
      ],
    },

    // ── 3 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-david',
      title: 'The Heart',
      where: '1 Samuel 8–17; 2 Samuel 5–7, 11–12',
      era: 'c. 1050–970 BC',
      artCaption: "A horn of oil poured out over the youngest son in Bethlehem",
      scenes: [
        "The people demand a king, so they can be like the other nations. Samuel tells them what a king will cost, and one verb runs through the whole speech. He will take. Your sons for his chariots. Your daughters for his kitchens. Your best fields. A tenth of everything you own. And on the day you cry out about the king you chose, Samuel says, God will not answer. They want one anyway. God’s own comment is the quietest line in the chapter. It is not Samuel they have rejected. It is him.",

        "They get Saul: tall, impressive, hollow. He is from Benjamin, the smallest tribe, and he stands a head above every man in the country. At his own coronation he cannot be found, and is eventually discovered hiding among the baggage. He starts well and then unravels. He offers a sacrifice rather than wait for Samuel. He spares the best of the plunder and calls it religion. Samuel’s reply is the verdict on that whole way of handling God: *to obey is better than sacrifice.* Saul reigns for years knowing the kingdom is already gone.",

        "Samuel is sent to a shepherd’s family in Bethlehem. Seven older brothers are brought out, and any of them would have looked the part. God stops him at the eldest: *man sees the outward appearance, but the LORD looks at the heart.* The youngest has not been called in. He is out with the sheep. The wry part: when David arrives, the narrator calls him handsome, with fine eyes. Looks are not the problem. They are not the test. Samuel pours oil from a horn over him. Messiah means anointed. Here it means a king.",

        "Goliath is a puzzle before he is a giant. The standard Hebrew makes him six cubits and a span, close to nine foot nine. The old Greek translation and a copy of Samuel found among the Dead Sea Scrolls both read four cubits and a span, about six foot nine. Either way, his bronze coat weighs some fifty-seven kilos. David tries Saul’s armour and cannot walk in it. And a sling was not a toy. Slingers were regular troops. David brings a weapon that works at a distance against a man built for close quarters.",

        "As king he does two things that outlast him. He takes Jerusalem, a hill fort no tribe had ever captured, and builds his capital on ground none of them could claim. Then he brings the ark into it, dancing in front of it in a way his wife finds humiliating. And he offers to build God a house. The answer comes through the prophet Nathan, reversed. David will not build God a house. God will build David one — a family line, a throne established forever. Every later hope for a messiah rests on that sentence.",

        "2 Samuel does not tidy him up. In the season when kings went to war, David stayed home. He sees a woman bathing, sends for her, and when she is pregnant, has her husband killed at the front. Uriah is a Hittite, a foreigner, one of David’s thirty-seven finest soldiers. Nathan corners him with a story: a rich man steals a poor man’s lamb. David passes sentence on himself, then says it: *I have sinned.* The child dies. His sons spend the rest of his reign doing to each other what he did to Uriah.",
      ],
      verseId: '1sa-16-7',
      takeaway: 'God chose by the heart — and the man he chose was forgiven, not flawless.',
      questions: [
        {
          id: 'sq-david-1',
          question: 'What does Samuel warn the people a king will do to them?',
          options: [
            'Take their sons, their daughters, their best fields and a tenth of everything',
            'Forbid them to keep the sabbath',
            'Move them out of the villages and into fortified cities',
            'Abolish the priesthood and sacrifice himself',
          ],
          answer: 0,
          explanation:
            '1 Samuel 8. The verb “take” runs through the speech like a drumbeat, and it is the exact reverse of the God who has spent four books giving. They ask for a king anyway.',
        },
        {
          id: 'sq-david-2',
          question: 'Why was David not present when Samuel came to anoint one of Jesse’s sons?',
          options: [
            'He was serving as an armour-bearer in Saul’s court',
            'He had been sent to buy grain in Moab',
            'He was carrying supplies to his brothers at the front',
            'He was the youngest, and was out with the sheep',
          ],
          answer: 3,
          explanation:
            'Seven brothers are presented and Samuel has to ask whether there are any more. The correction in 1 Samuel 16:7 lands while the prophet is on the point of anointing the tallest man in the room.',
        },
        {
          id: 'sq-david-3',
          question: 'How do the oldest manuscripts differ about Goliath’s height?',
          options: [
            'They all agree on six cubits and a span',
            'Only the Latin gives any height at all',
            'The Greek translation and a Dead Sea Scrolls copy read four cubits and a span — about six foot nine — where the standard Hebrew reads six cubits',
            'The Hebrew gives no height, and the figure comes from Josephus',
          ],
          answer: 2,
          explanation:
            'The shorter reading survives in the earlier witnesses, and it does not shrink the story: a man of six foot nine in full bronze armour facing an unarmoured teenager was still a wildly uneven contest.',
        },
        {
          id: 'sq-david-4',
          question: 'What does God promise David through the prophet Nathan?',
          options: [
            'That Israel will never again lose a battle',
            'That God will build David a house — a dynasty whose throne is established forever',
            'That his son will be the last king Israel needs',
            'That the temple, once built, will never fall',
          ],
          answer: 1,
          explanation:
            'David offers to build God a house and is told the gift runs the other way. The promise is where the title “son of David” comes from, and every later messianic hope in the Bible is an argument about this sentence.',
        },
      ],
    },

    // ── 4 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-divided',
      title: 'Wisdom, Temple, Split',
      where: '1 Kings',
      era: 'c. 970–930 BC',
      artCaption: 'Cedar, gold, and a kingdom broken in half at Shechem',
      scenes: [
        "Solomon does not inherit the throne cleanly. His older brother Adonijah has already declared himself king when Nathan and Bathsheba move on David’s deathbed. The reign opens with a short, cold run of executions. Then Solomon goes to Gibeon to sacrifice, and God offers him anything. He asks for a heart that listens, so he can judge his people well. He is given it, along with the wealth and reputation he did not ask for. The proof comes at once: two women, one living baby, and a ruling built to make the truth show itself.",

        "He builds the temple, and 1 Kings dates it precisely: the fourth year of his reign, four hundred and eighty years after Israel came out of Egypt. It takes seven years. His own palace, described in the very next chapter, takes thirteen. The temple itself is small — about twenty-seven metres by nine, the footprint of a modest parish church. It was never meant to hold a congregation. People gathered in the courtyards outside. The stone was cut at the quarry and carried up finished, so no iron tool was heard on the site.",

        "At the dedication Solomon prays the most surprising prayer in the book. He has just spent seven years on a house for God, and opens by admitting it cannot work. *Heaven and the heaven of heavens can’t contain you; how much less this house which I have built!* God had lived in a tent since Sinai. This is his first permanent address, and the man who paid for it says out loud that God does not live in it. He then asks that when a foreigner prays towards this house, God would hear him too.",

        "Then he accumulates. Deuteronomy had given a king three rules: not many horses, not many wives, not much gold. 1 Kings works through them like a checklist. Fourteen hundred chariots and twelve thousand horsemen, the horses imported from Egypt. Six hundred and sixty-six talents of gold in one year. Seven hundred wives and three hundred concubines, most of them sealing treaties with neighbouring courts. Shrines for their gods go up on the hill east of Jerusalem, and stand there about three hundred years. The wisest man in the Old Testament ends up hedging his bets.",

        "His son inherits a kingdom and a grievance about forced labour. The northern tribes meet Rehoboam at Shechem and ask him to lighten the load his father imposed. The older advisers tell him to say yes. The men he grew up with tell him to say something else, and he does. My father whipped you; I will use scorpions. The answer comes back as a slogan: what share do we have in David? They go home. Rehoboam sends the officer over the labour gangs to reason with them, and they stone him to death.",

        "Within a chapter the nation is two. Israel in the north takes ten tribes, and sets up golden calves at Bethel and Dan so that nobody needs to go to Jerusalem. Judah in the south keeps the city, the temple and David’s line. Five years later an Egyptian pharaoh named Shishak walks in and strips the treasury. Rehoboam replaces the palace guard’s gold shields with bronze ones, and has them carried in and out as though nothing has changed. From here the Old Testament tells two declining stories at once, and sends prophets into both.",
      ],
      verseId: '1ki-3-9',
      takeaway: 'The golden age lasted one generation, and ended in compromise.',
      questions: [
        {
          id: 'sq-divided-1',
          question: 'What did Solomon ask God for when he was offered anything?',
          options: [
            'Long life',
            'Riches and honour',
            'A heart that listens, so that he could judge his people well',
            'Victory over his enemies',
          ],
          answer: 2,
          explanation:
            '1 Kings 3:9, where the Hebrew is literally “a hearing heart”. He is given the wisdom he asked for and the wealth he did not, and it is the wealth that eventually undoes him.',
        },
        {
          id: 'sq-divided-2',
          question: 'How long did the temple take to build, compared with Solomon’s own palace?',
          options: [
            'Seven years for the temple, thirteen for the palace',
            'Thirteen years for the temple, seven for the palace',
            'Twenty years for each of them',
            'Three years for the temple; the palace was never finished',
          ],
          answer: 0,
          explanation:
            '1 Kings prints the two figures one chapter apart and lets them sit there without comment. The temple was also small — the crowds worshipped in the courtyards, never inside the building.',
        },
        {
          id: 'sq-divided-3',
          question: 'What broke the kingdom in two after Solomon died?',
          options: [
            'A dispute over which of Solomon’s sons should reign',
            'An Egyptian invasion that cut the country in half',
            'A prophet crowning a rival king inside Jerusalem',
            'Rehoboam refused to ease the forced labour his father had imposed',
          ],
          answer: 3,
          explanation:
            'The northern tribes asked for relief at Shechem and were promised scorpions instead. The labour gangs that raised the temple were the same grievance that split the nation one generation later.',
        },
      ],
    },
  ],
};
